import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    getArticleTemplateById,
    putArticleTemplate,
} from "../../app/services/articleTemplateService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";

import Form from "react-bootstrap/Form";
import { Alert, Breadcrumb, Button, Col, Row } from "react-bootstrap";
import { ArticleTemplate, defaultArticleTemplate } from "../../models/responses/articleTemplate";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { ArticleType } from "../../models/enums/articleTypeEnum";

interface Props { }

const ArticleTemplateEdit: FC<Props> = () => {
    const { articleTemplateId } = useParams<{ articleTemplateId: string }>();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [displayError, setDisplayError] = useState(false);
    const isWaitingArticleTemplatePutResponse = useSelector(
        (state: RootState) => state.articleTemplate.isWaitingArticleTemplatePutResponse
    );
    const articleTemplate = useSelector((state: RootState) => state.articleTemplate.articleTemplate);
    const [articleTemplateData, setArticleTemplateData] = useState<ArticleTemplate>(defaultArticleTemplate);

    useEffect(() => {
        dispatch(getArticleTemplateById(instance, account, articleTemplateId));
    }, [account, accounts, dispatch, instance, articleTemplateId]);
    useEffect(() => {
        if (articleTemplate) {
            setArticleTemplateData({
                id: articleTemplate.id,
                description: articleTemplate.description,
                articleNumber: articleTemplate.articleNumber,
                salesPrice: articleTemplate.salesPrice,
                keepIncomingPrice: articleTemplate.keepIncomingPrice,
                articleType: articleTemplate.articleType
            });
        }
    }, [articleTemplate]);

    const handleUpdateArticleTemplate = () => {
        setDisplayError(false);
        dispatch(putArticleTemplate(instance, account, articleTemplateData, onSuccess));
    };

    const onSuccess = (articleTemplateId: string | null) => {
        if (articleTemplateId === null) {
            setDisplayError(true);
            return;
        }

        setTimeout(() => {
            navigate(`../../${articleTemplateId}`, { relative: "path" });
        }, 500);
    };

    return (
        <div>
            <Breadcrumb>
                <Breadcrumb.Item href="/admin/article-templates">Artikelmallar</Breadcrumb.Item>
                <Breadcrumb.Item active>Uppdatera artikelmall</Breadcrumb.Item>
            </Breadcrumb>
            <hr />
            <Form className="w-3">
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Artikelnummer *</Form.Label>
                            <Form.Control
                                type="text"
                                value={articleTemplateData.articleNumber}
                                onInput={(e) => setArticleTemplateData({ ...articleTemplateData, articleNumber: e.currentTarget.value })}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Beskrivning *</Form.Label>
                            <Form.Control
                                type="text"
                                value={articleTemplateData.description}
                                onInput={(e) => setArticleTemplateData({ ...articleTemplateData, description: e.currentTarget.value })}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Försäljningspris</Form.Label>
                            <Form.Control
                                type="text"
                                value={articleTemplateData.salesPrice}
                                onInput={(e) => setArticleTemplateData({ ...articleTemplateData, salesPrice: e.currentTarget.value })}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Artikeltyp</Form.Label>
                            <Form.Select
                                value={articleTemplateData.articleType}
                                onChange={(e) => setArticleTemplateData({
                                    ...articleTemplateData,
                                    articleType: Number(e.currentTarget.value) as ArticleType
                                })}
                            >
                                {Object.values(ArticleType)
                                    .filter((v) => typeof v === "number")
                                    .map((value) => (
                                    <option key={value} value={value}>
                                        {ArticleType[value as number]}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Använd ursprungligt pris"
                                checked={articleTemplateData.keepIncomingPrice}
                                onChange={(e) => setArticleTemplateData({ ...articleTemplateData, keepIncomingPrice: e.currentTarget.checked })}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <div className="text-end">
                    <Button
                        variant="primary"
                        className="mb-3"
                        onClick={handleUpdateArticleTemplate}
                        disabled={
                            !(
                                articleTemplateData.articleNumber !== undefined &&
                                articleTemplateData.articleNumber.length > 0 &&
                                articleTemplateData.description !== undefined &&
                                articleTemplateData.description.length > 0 &&
                                !isWaitingArticleTemplatePutResponse
                            )
                        }
                    >
                        Uppdatera
                    </Button>
                </div>

                <Alert key="alert" variant="danger" show={displayError}>
                    <p>Något gick fel, kontrollera uppgifterna och försök igen.</p>
                </Alert>
            </Form>
        </div>
    );
};

export default ArticleTemplateEdit;
